import { Link2 } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { toEmbedUrl, validateMapUrl, validateHttpsUrl } from "@sections/embedUtils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EmbedMode = "video" | "map" | "custom";

interface EmbedUrlFieldProps {
  mode: EmbedMode;
  /** Current prop values from the section. */
  props: Record<string, unknown>;
  /** Callback to update a single section prop. */
  updateProp: (propName: string, value: unknown) => void;
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

const DEBOUNCE_MS = 400;

function validateForMode(
  mode: EmbedMode,
  url: string,
): { valid: boolean; error: string | null; normalized: string | null } {
  if (!url || url === "about:blank") {
    return { valid: true, error: null, normalized: null };
  }

  switch (mode) {
    case "video": {
      const normalized = toEmbedUrl(url);
      if (normalized === "about:blank") {
        return {
          valid: false,
          error: "Only YouTube and Vimeo URLs are supported",
          normalized: null,
        };
      }
      return { valid: true, error: null, normalized };
    }
    case "map": {
      if (!validateMapUrl(url)) {
        return {
          valid: false,
          error: "Only Google Maps embed URLs are supported",
          normalized: null,
        };
      }
      return { valid: true, error: null, normalized: url };
    }
    case "custom": {
      if (!validateHttpsUrl(url)) {
        return {
          valid: false,
          error: "Enter a valid HTTPS URL",
          normalized: null,
        };
      }
      return { valid: true, error: null, normalized: url };
    }
  }
}

// ---------------------------------------------------------------------------
// Shared input class
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  "w-full border border-ink-6 rounded-[9px] bg-paper px-[11px] py-2 text-[12px] text-ink focus:outline-none focus:border-tide focus:ring-[3px] focus:ring-tide-s transition-all";
const LABEL_CLASS =
  "block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]";

// ---------------------------------------------------------------------------
// Empty-state hint
// ---------------------------------------------------------------------------

function EmbedHint({ text }: { text: string }) {
  return (
    <div className="flex gap-2.5 bg-paper rounded-[10px] px-3 py-2.5 text-[12px] leading-relaxed text-ink-4 mb-[18px]">
      <Link2
        size={14}
        strokeWidth={2}
        className="flex-none mt-0.5 text-tide"
      />
      <span>{text}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components by mode
// ---------------------------------------------------------------------------

function VideoEmbedFields({ props, updateProp }: Omit<EmbedUrlFieldProps, "mode">) {
  const urlId = useId();
  const raw = (props.videoUrl as string) || "";
  const currentUrl = raw === "about:blank" ? "" : raw;
  const [draft, setDraft] = useState(currentUrl);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Clean up debounce timer on unmount
  useEffect(() => () => { clearTimeout(timerRef.current); }, []);

  // Sync draft when prop changes externally
  useEffect(() => {
    setDraft(currentUrl);
    if (currentUrl && currentUrl !== "about:blank") {
      const result = validateForMode("video", currentUrl);
      setPreviewUrl(result.normalized);
      setError(null);
    } else {
      setPreviewUrl(null);
      setError(null);
    }
  }, [currentUrl]);

  const handleChange = useCallback(
    (value: string) => {
      setDraft(value);
      clearTimeout(timerRef.current);

      if (!value.trim()) {
        setError(null);
        setPreviewUrl(null);
        updateProp("videoUrl", "about:blank");
        return;
      }

      timerRef.current = setTimeout(() => {
        const result = validateForMode("video", value);
        setError(result.error);
        if (result.valid && result.normalized) {
          setPreviewUrl(result.normalized);
          updateProp("videoUrl", value);
        } else {
          setPreviewUrl(null);
        }
      }, DEBOUNCE_MS);
    },
    [updateProp],
  );

  return (
    <>
      {!currentUrl && (
        <EmbedHint text="Paste a video link to preview it on your page." />
      )}
      <div className="mb-[18px]">
        <label htmlFor={urlId} className={LABEL_CLASS}>
          Video URL
        </label>
        <input
          id={urlId}
          type="text"
          value={draft}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Paste a YouTube or Vimeo link"
          className={INPUT_CLASS}
        />
        {error && (
          <p className="mt-1.5 text-[11px] text-rust font-medium">{error}</p>
        )}
        {previewUrl && !error && (
          <p className="mt-1.5 text-[10.5px] text-ink-5 font-mono truncate">
            {previewUrl}
          </p>
        )}
      </div>
    </>
  );
}

function MapEmbedFields({ props, updateProp }: Omit<EmbedUrlFieldProps, "mode">) {
  const addressId = useId();
  const embedId = useId();
  const heightId = useId();
  const currentAddress = (props.address as string) || "";
  const currentEmbedUrl = (props.embedUrl as string) || "";
  const currentHeight = (props.height as number) || 400;
  const showAddress = props.showAddress !== false;

  const [addressDraft, setAddressDraft] = useState(currentAddress);
  const [embedDraft, setEmbedDraft] = useState(currentEmbedUrl);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const addressTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const embedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Clean up debounce timers on unmount
  useEffect(() => () => { clearTimeout(addressTimerRef.current); clearTimeout(embedTimerRef.current); }, []);

  // Sync drafts when props change externally
  useEffect(() => {
    setAddressDraft(currentAddress);
  }, [currentAddress]);

  useEffect(() => {
    setEmbedDraft(currentEmbedUrl);
    if (currentEmbedUrl) {
      const result = validateForMode("map", currentEmbedUrl);
      if (result.valid) {
        setPreviewUrl(result.normalized);
        setError(null);
      }
    } else {
      // Show auto-generated preview from address
      if (currentAddress) {
        setPreviewUrl(
          `https://maps.google.com/maps?q=${encodeURIComponent(currentAddress)}&output=embed`,
        );
      }
      setError(null);
    }
  }, [currentEmbedUrl, currentAddress]);

  const handleAddressChange = useCallback(
    (value: string) => {
      setAddressDraft(value);
      clearTimeout(addressTimerRef.current);

      addressTimerRef.current = setTimeout(() => {
        updateProp("address", value);
        // If no explicit embed URL, update the preview to the auto-generated one
        if (!embedDraft) {
          setPreviewUrl(
            value
              ? `https://maps.google.com/maps?q=${encodeURIComponent(value)}&output=embed`
              : null,
          );
        }
      }, DEBOUNCE_MS);
    },
    [updateProp, embedDraft],
  );

  const handleEmbedChange = useCallback(
    (value: string) => {
      setEmbedDraft(value);
      clearTimeout(embedTimerRef.current);

      if (!value.trim()) {
        setError(null);
        updateProp("embedUrl", undefined);
        // Fall back to auto-generated from address
        if (addressDraft) {
          setPreviewUrl(
            `https://maps.google.com/maps?q=${encodeURIComponent(addressDraft)}&output=embed`,
          );
        } else {
          setPreviewUrl(null);
        }
        return;
      }

      embedTimerRef.current = setTimeout(() => {
        const result = validateForMode("map", value);
        setError(result.error);
        if (result.valid && result.normalized) {
          setPreviewUrl(result.normalized);
          updateProp("embedUrl", value);
        } else {
          setPreviewUrl(null);
        }
      }, DEBOUNCE_MS);
    },
    [updateProp, addressDraft],
  );

  return (
    <>
      {!currentAddress && !currentEmbedUrl && (
        <EmbedHint text="Enter an address to show a map on your page." />
      )}

      {/* Address */}
      <div className="mb-[18px]">
        <label htmlFor={addressId} className={LABEL_CLASS}>
          Address
        </label>
        <input
          id={addressId}
          type="text"
          value={addressDraft}
          onChange={(e) => handleAddressChange(e.target.value)}
          placeholder="123 Main St, City, State"
          className={INPUT_CLASS}
        />
        <p className="mt-1 text-[10.5px] text-ink-5">
          A map will be generated from this address unless you paste a custom embed URL below.
        </p>
      </div>

      {/* Embed URL */}
      <div className="mb-[18px]">
        <label htmlFor={embedId} className={LABEL_CLASS}>
          Embed URL
        </label>
        <input
          id={embedId}
          type="text"
          value={embedDraft}
          onChange={(e) => handleEmbedChange(e.target.value)}
          placeholder="Paste a Google Maps embed URL (optional)"
          className={INPUT_CLASS}
        />
        {error && (
          <p className="mt-1.5 text-[11px] text-rust font-medium">{error}</p>
        )}
        {previewUrl && !error && (
          <p className="mt-1.5 text-[10.5px] text-ink-5 font-mono truncate">
            {previewUrl}
          </p>
        )}
      </div>

      {/* Height */}
      <div className="mb-[18px]">
        <label htmlFor={heightId} className={LABEL_CLASS}>
          Map height (px)
        </label>
        <input
          id={heightId}
          type="number"
          min={200}
          value={currentHeight}
          onChange={(e) =>
            updateProp("height", Math.max(200, Number(e.target.value) || 200))
          }
          className={INPUT_CLASS}
        />
      </div>

      {/* Show address */}
      <div className="mb-[18px]">
        <label className="flex items-center gap-2.5 cursor-pointer group">
          <input
            type="checkbox"
            checked={showAddress}
            onChange={(e) => updateProp("showAddress", e.target.checked)}
            className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer"
          />
          <span className="text-[12px] font-medium text-ink-3 group-hover:text-ink-2 transition-colors">
            Show address alongside map
          </span>
        </label>
      </div>
    </>
  );
}

function CustomEmbedFields({ props, updateProp }: Omit<EmbedUrlFieldProps, "mode">) {
  const urlId = useId();
  const heightId = useId();
  const allowId = useId();
  const raw = (props.url as string) || "";
  const currentUrl = raw === "about:blank" ? "" : raw;
  const currentHeight = (props.height as number) || 400;
  const currentAllow = (props.allow as string) || "";

  const [draft, setDraft] = useState(currentUrl);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Clean up debounce timer on unmount
  useEffect(() => () => { clearTimeout(timerRef.current); }, []);

  // Sync draft when prop changes externally
  useEffect(() => {
    setDraft(currentUrl);
    if (currentUrl && currentUrl !== "about:blank") {
      const result = validateForMode("custom", currentUrl);
      if (result.valid) {
        setPreviewUrl(result.normalized);
        setError(null);
      }
    } else {
      setPreviewUrl(null);
      setError(null);
    }
  }, [currentUrl]);

  const handleChange = useCallback(
    (value: string) => {
      setDraft(value);
      clearTimeout(timerRef.current);

      if (!value.trim()) {
        setError(null);
        setPreviewUrl(null);
        updateProp("url", "about:blank");
        return;
      }

      timerRef.current = setTimeout(() => {
        const result = validateForMode("custom", value);
        setError(result.error);
        if (result.valid && result.normalized) {
          setPreviewUrl(result.normalized);
          updateProp("url", value);
        } else {
          setPreviewUrl(null);
        }
      }, DEBOUNCE_MS);
    },
    [updateProp],
  );

  return (
    <>
      {!currentUrl && (
        <EmbedHint text="Paste an embed URL to preview it on your page." />
      )}

      {/* URL */}
      <div className="mb-[18px]">
        <label htmlFor={urlId} className={LABEL_CLASS}>
          Embed URL
        </label>
        <input
          id={urlId}
          type="text"
          value={draft}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Paste an embed URL"
          className={INPUT_CLASS}
        />
        {error && (
          <p className="mt-1.5 text-[11px] text-rust font-medium">{error}</p>
        )}
        {previewUrl && !error && (
          <p className="mt-1.5 text-[10.5px] text-ink-5 font-mono truncate">
            {previewUrl}
          </p>
        )}
      </div>

      {/* Height */}
      <div className="mb-[18px]">
        <label htmlFor={heightId} className={LABEL_CLASS}>
          Embed height (px)
        </label>
        <input
          id={heightId}
          type="number"
          min={100}
          value={currentHeight}
          onChange={(e) =>
            updateProp("height", Math.max(100, Number(e.target.value) || 100))
          }
          className={INPUT_CLASS}
        />
      </div>

      {/* Allow (iframe permissions) */}
      <div className="mb-[18px]">
        <label htmlFor={allowId} className={LABEL_CLASS}>
          Iframe permissions
        </label>
        <input
          id={allowId}
          type="text"
          value={currentAllow}
          onChange={(e) => updateProp("allow", e.target.value || undefined)}
          placeholder="e.g. autoplay; fullscreen"
          className={INPUT_CLASS}
        />
        <p className="mt-1 text-[10.5px] text-ink-5">
          Controls what the embedded page can do (e.g., autoplay, camera, microphone). Leave blank for defaults.
        </p>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/** Embed URL editing field group for VideoEmbed, MapEmbed, and CustomEmbed. */
export function EmbedUrlField({ mode, props, updateProp }: EmbedUrlFieldProps) {
  switch (mode) {
    case "video":
      return <VideoEmbedFields props={props} updateProp={updateProp} />;
    case "map":
      return <MapEmbedFields props={props} updateProp={updateProp} />;
    case "custom":
      return <CustomEmbedFields props={props} updateProp={updateProp} />;
  }
}
